import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import debounce from 'lodash/debounce';
import { FilterBar, useFilterBarStyles } from '../ui';
import { AAV_ORDER_STATUS_OPTIONS } from '../../lib/aavOrderStatus';
import { dateParam, digitsOnly } from '../../lib/orderFilters';

const SORT_DIRECTIONS = [
    { value: 'DESC', label: 'Newest first' },
    { value: 'ASC', label: 'Oldest first' },
];

interface Props {
    queryParams: any;
    loadOrders: (params: any) => void;
    loading: boolean;
    /** Resets every filter; the parent remounts this component afterwards so the text fields empty. */
    onClear: () => void;
}

/** Filter strip of the AAV orders list. Every change reloads page 1 with the new params. */
export default function AavOrdersFilters({ queryParams, loadOrders, loading, onClear }: Props) {
    const classes = useFilterBarStyles();
    const [search, setSearch] = useState<string>(() => digitsOnly(String(queryParams?.search ?? '')));
    const [searchError, setSearchError] = useState<string | null>(null);

    const latest = useRef({ queryParams, loadOrders });
    latest.current = { queryParams, loadOrders };

    const handleFilterChange = (name: string, value: any) => {
        latest.current.loadOrders({ ...latest.current.queryParams, page: 1, [name]: value });
    };

    const debouncedSearch = useMemo(() => debounce((value: string) => {
        if (value.length > 0 && value.length < 3) {
            setSearchError('Type at least 3 digits');
            return;
        }
        setSearchError(null);
        handleFilterChange('search', value || null);
    }, 500), []);
    const debouncedTags = useMemo(() => debounce((value: string) => handleFilterChange('tags', value || null), 500), []);
    useEffect(() => () => {
        debouncedSearch.cancel();
        debouncedTags.cancel();
    }, [debouncedSearch, debouncedTags]);

    // Order numbers are numeric: letters and symbols are dropped as they are typed or pasted.
    const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const digits = digitsOnly(event.target.value);
        setSearch(digits);
        debouncedSearch(digits);
    };

    // Clearing a date sends null; a half-typed date is ignored until it is valid.
    const onDateChange = (name: 'createdAtMin' | 'createdAtMax') => (value: Dayjs.Dayjs | null) => {
        if (value === null) {
            handleFilterChange(name, null);
            return;
        }
        const iso = dateParam(value);
        if (iso) handleFilterChange(name, iso);
    };

    return (
        <FilterBar label="AAV order filters">
            <TextField
                className={classes.search}
                size="small"
                label="Search by order number"
                name="search"
                value={search}
                disabled={loading}
                onChange={onSearchChange}
                error={Boolean(searchError)}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <FormControl className={classes.select} size="small">
                <InputLabel id="aav-status-label">Status</InputLabel>
                <Select
                    labelId="aav-status-label"
                    label="Status"
                    name="statusId"
                    value={queryParams?.statusId ?? ''}
                    disabled={loading}
                    onChange={(event) => handleFilterChange('statusId', event.target.value || null)}
                >
                    <MenuItem value="">All</MenuItem>
                    {AAV_ORDER_STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <TextField
                className={classes.text}
                size="small"
                label="Tags"
                name="tags"
                defaultValue={queryParams?.tags || ''}
                placeholder="tag1, tag2"
                disabled={loading}
                onChange={(event) => debouncedTags(event.target.value)}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    className={classes.date}
                    label="From"
                    format="YYYY-MM-DD"
                    value={queryParams?.createdAtMin ? Dayjs(queryParams.createdAtMin) : null}
                    maxDate={queryParams?.createdAtMax ? Dayjs(queryParams.createdAtMax) : Dayjs(new Date())}
                    disabled={loading}
                    onChange={onDateChange('createdAtMin')}
                    slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                    className={classes.date}
                    label="To"
                    format="YYYY-MM-DD"
                    value={queryParams?.createdAtMax ? Dayjs(queryParams.createdAtMax) : null}
                    maxDate={Dayjs(new Date())}
                    disabled={loading}
                    onChange={onDateChange('createdAtMax')}
                    slotProps={{ textField: { size: 'small' } }}
                />
            </LocalizationProvider>
            <FormControl className={classes.select} size="small">
                <InputLabel id="aav-sort-label">Sort</InputLabel>
                <Select
                    labelId="aav-sort-label"
                    label="Sort"
                    name="sortDirection"
                    value={queryParams?.sortDirection || 'DESC'}
                    disabled={loading}
                    onChange={(event) => handleFilterChange('sortDirection', event.target.value)}
                >
                    {SORT_DIRECTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Button className={classes.clear} variant="text" size="small" onClick={onClear} disabled={loading}>
                Clear filters
            </Button>
            {searchError && <p className={classes.hint} role="alert">{searchError}</p>}
        </FilterBar>
    );
}
